/**
 * The mech movement rules: one deterministic step of a kinematic mech (ADR-0002). Pure over plain
 * state; the only side effect is the collision query through `world.moveCapsule`. Combat feeds in
 * per-tick `MoveModifiers` (slows, stuns, dashes, knockbacks); without them the step is Phase 2's.
 */
import { SIM_DT } from '../constants.js';
import { BACKPEDAL_FACTOR, COYOTE_TICKS, ENERGY_REGEN_DELAY_TICKS, GRAVITY, IDLE_SPEED, TERMINAL_FALL_SPEED, } from './constants.js';
import { clamp, moveToward2D, turnToward } from './math.js';
export function capsuleOf(def) {
    return {
        radius: def.capsule.radius,
        halfHeight: Math.max(0, def.capsule.height / 2 - def.capsule.radius),
        stepHeight: def.capsule.stepHeight,
        snapDistance: def.capsule.snapDistance,
    };
}
export function createMechState(def, spawn, yaw = 0) {
    return {
        tick: 0,
        position: { ...spawn },
        velocity: { x: 0, y: 0, z: 0 },
        yaw,
        aimYaw: yaw,
        aimPitch: 0,
        energy: def.energy,
        grounded: false,
        airTicks: 0,
        regenDelayTicks: 0,
        boosting: false,
        jetting: false,
        jumpHeld: false,
        alive: true,
    };
}
export const IDLE_INPUT = Object.freeze({
    seq: 0,
    moveX: 0,
    moveY: 0,
    aimYaw: 0,
    aimPitch: 0,
    boost: false,
    jump: false,
    primary: false,
    secondary: false,
    ability: false,
    ultimate: false,
    vent: false,
    melee: false,
});
export const DEFAULT_MOVE_MODIFIERS = Object.freeze({
    speedScale: 1,
    turnScale: 1,
    jetThrustScale: 1,
    jetCostScale: 1,
    noBoost: false,
    noJump: false,
    immobile: false,
    noTurn: false,
    dash: null,
    impulse: null,
});
const scratch = {
    x: 0,
    y: 0,
    z: 0,
    grounded: false,
    hitWall: false,
    hitCeiling: false,
};
export function stepMech(prev, input, world, def, dt = SIM_DT, mods = DEFAULT_MOVE_MODIFIERS) {
    const capsule = capsuleOf(def);
    const aimYaw = input.aimYaw;
    const aimPitch = clamp(input.aimPitch, -Math.PI / 2, Math.PI / 2);
    const yaw = mods.noTurn
        ? prev.yaw
        : turnToward(prev.yaw, aimYaw, def.turnRateRad * mods.turnScale * dt);
    // --- desired planar velocity, relative to the aim direction ---
    let mx = mods.immobile ? 0 : clamp(input.moveX, -1, 1);
    let my = mods.immobile ? 0 : clamp(input.moveY, -1, 1);
    const mag = Math.hypot(mx, my);
    if (mag > 1) {
        mx /= mag;
        my /= mag;
    }
    const moving = mag > 0.001;
    const sinYaw = Math.sin(aimYaw);
    const cosYaw = Math.cos(aimYaw);
    // forward = (-sin, -cos), right = (cos, -sin) in the XZ plane
    const dirX = cosYaw * mx - sinYaw * my;
    const dirZ = -sinYaw * mx - cosYaw * my;
    let energy = prev.energy;
    const canBoost = !mods.noBoost && (prev.boosting ? energy >= 1 : energy >= def.boostEngageEnergy);
    const boosting = input.boost && moving && canBoost && mods.dash === null;
    const maxSpeed = (boosting ? def.boostSpeed : def.speed) * (my < 0 ? BACKPEDAL_FACTOR : 1) * mods.speedScale;
    const targetX = dirX * maxSpeed;
    const targetZ = dirZ * maxSpeed;
    const accelPerSec = prev.grounded
        ? moving
            ? def.acceleration
            : def.deceleration
        : def.acceleration * def.airControl;
    let vx;
    let vz;
    if (mods.dash) {
        vx = mods.dash.vx;
        vz = mods.dash.vz;
    }
    else {
        const planar = moveToward2D(prev.velocity.x, prev.velocity.z, targetX, targetZ, accelPerSec * dt);
        vx = planar.x;
        vz = planar.z;
    }
    let regenDelayTicks = Math.max(0, prev.regenDelayTicks - 1);
    if (boosting) {
        energy = Math.max(0, energy - def.boostCost * dt);
        regenDelayTicks = ENERGY_REGEN_DELAY_TICKS;
    }
    // --- vertical: jump launch, jet thrust while held, gravity ---
    let vy = prev.velocity.y;
    let jetting = false;
    const jumpAllowed = input.jump && !mods.noJump && !mods.immobile;
    const jumpEdge = jumpAllowed && !prev.jumpHeld;
    const canLaunch = prev.grounded || prev.airTicks <= COYOTE_TICKS;
    const launchCost = def.jumpJets.energyCost * mods.jetCostScale;
    const thrustCostPerSecond = def.jumpJets.thrustEnergyPerSecond * mods.jetCostScale;
    if (jumpEdge && canLaunch && energy >= launchCost) {
        vy = def.jumpJets.launchVelocity;
        energy -= launchCost;
        regenDelayTicks = ENERGY_REGEN_DELAY_TICKS;
        jetting = true;
    }
    else if (jumpAllowed &&
        prev.jetting &&
        !prev.grounded &&
        (energy > 0 || thrustCostPerSecond === 0)) {
        vy += def.jumpJets.thrust * mods.jetThrustScale * dt;
        energy = Math.max(0, energy - thrustCostPerSecond * dt);
        regenDelayTicks = ENERGY_REGEN_DELAY_TICKS;
        jetting = true;
    }
    let launched = vy > 0 && jetting;
    if (mods.impulse) {
        vx += mods.impulse.x;
        vz += mods.impulse.z;
        vy += mods.impulse.y;
        if (mods.impulse.y > 0)
            launched = true;
    }
    if (!prev.grounded || launched) {
        vy = Math.max(vy - GRAVITY * dt, -TERMINAL_FALL_SPEED);
    }
    else {
        // Grounded: a small downward bias keeps snap-to-ground engaged on slopes and steps.
        vy = -0.5;
    }
    // --- collision solve ---
    world.moveCapsule(prev.position, { x: vx * dt, y: vy * dt, z: vz * dt }, capsule, scratch);
    const position = { x: scratch.x, y: scratch.y, z: scratch.z };
    const actualVx = (position.x - prev.position.x) / dt;
    const actualVz = (position.z - prev.position.z) / dt;
    // Blocked by a wall: drop the velocity we could not realise so we do not keep pushing.
    if (scratch.hitWall) {
        vx = Math.abs(actualVx) < Math.abs(vx) ? actualVx : vx;
        vz = Math.abs(actualVz) < Math.abs(vz) ? actualVz : vz;
    }
    const grounded = scratch.grounded && vy <= 0;
    if (grounded)
        vy = 0;
    if (scratch.hitCeiling && vy > 0)
        vy = 0;
    if (!grounded && Math.abs(vy) < 1e-9)
        vy = 0;
    if (regenDelayTicks === 0 && !boosting && !jetting) {
        energy = Math.min(def.energy, energy + def.energyRegen * dt);
    }
    const speed = Math.hypot(vx, vz);
    return {
        tick: prev.tick + 1,
        position,
        velocity: { x: speed < IDLE_SPEED ? 0 : vx, y: vy, z: speed < IDLE_SPEED ? 0 : vz },
        yaw,
        aimYaw,
        aimPitch,
        energy,
        grounded,
        airTicks: grounded ? 0 : prev.airTicks + 1,
        regenDelayTicks,
        boosting,
        jetting: jetting && !grounded,
        jumpHeld: input.jump,
        alive: position.y > world.killY,
    };
}
//# sourceMappingURL=step-mech.js.map